import React, { useMemo } from 'react';
import {
  EventEntity,
  EventUserEntity,
  PartnerMatchResult,
  UserFacingTopic,
} from 'src/store/eventApi';
import { SeatAssignment, genderLabel } from './seatNumbers';
import { getAttendeeDisplayName } from 'src/util';

const FOOTER_TEXT = '本卡片由活动前访谈内容生成 · 话题仅供参考';
const TAGLINE_SUFFIX = '恋Ai脑 · 匹配卡';
const TOP_PICK_COUNT = 3;
const MAX_QUESTIONS_PER_ROW = 2;

interface PrintCardProps {
  event: EventEntity;
  attendee: EventUserEntity;
  attendeeMap: Map<string, EventUserEntity>;
  seatMap: Map<string, SeatAssignment>;
  matches: PartnerMatchResult[];
}

const findRecommendationTopic = (
  topics: UserFacingTopic[]
): UserFacingTopic | undefined => topics.find((t) => t.type === '推荐理由');

const partnerLabel = (
  partner: EventUserEntity | undefined,
  partnerId: string
): string => getAttendeeDisplayName(partner) || partnerId;

const seatFor = (
  seatMap: Map<string, SeatAssignment>,
  userId: string
): string => seatMap.get(userId)?.seat ?? '';

const PrintCard = ({
  event,
  attendee,
  attendeeMap,
  seatMap,
  matches,
}: PrintCardProps) => {
  const seat = seatFor(seatMap, attendee.userId);
  const gender = attendee.user?.profile?.gender;
  const name = getAttendeeDisplayName(attendee) || attendee.userId;
  const gLabel = genderLabel(gender);

  const topPicks = useMemo(() => {
    const picks: Array<{
      match: PartnerMatchResult;
      recoTopic: UserFacingTopic;
    }> = [];
    const collect = (predicate: (m: PartnerMatchResult) => boolean) => {
      for (const m of matches) {
        if (picks.length >= TOP_PICK_COUNT) return;
        if (!predicate(m)) continue;
        const recoTopic = findRecommendationTopic(m.topics);
        if (!recoTopic) continue;
        picks.push({ match: m, recoTopic });
      }
    };
    collect((m) => !m.hasDealbreaker);
    collect((m) => m.hasDealbreaker);
    return picks;
  }, [matches]);

  const topPickIds = useMemo(
    () => new Set(topPicks.map((p) => p.match.resultId)),
    [topPicks]
  );

  const orderedMatches = useMemo(() => {
    const withSeat = matches.map((m) => ({
      match: m,
      seat: seatFor(seatMap, m.partnerId),
    }));
    return withSeat.sort((a, b) => a.seat.localeCompare(b.seat));
  }, [matches, seatMap]);

  return (
    <div
      className="print-card"
      style={{
        maxWidth: 800,
        margin: '0 auto 40px',
        padding: 32,
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
      }}
    >
      <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>
        {event.title} · {TAGLINE_SUFFIX}
      </div>
      <div
        style={{
          borderBottom: '2px solid #1a202c',
          paddingBottom: 8,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
          {name}（{gLabel ? `${gLabel}·` : ''}
          {seat}）
        </div>
      </div>

      {topPicks.length > 0 && (
        <div
          style={{
            background: '#f7fafc',
            padding: 16,
            marginBottom: 20,
            borderRadius: 4,
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#2b6cb0',
              marginBottom: 8,
            }}
          >
            AI 今晚为你重点标注
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {topPicks.map(({ match, recoTopic }) => {
              const partner = attendeeMap.get(match.partnerId);
              const pSeat = seatFor(seatMap, match.partnerId);
              const pName = partnerLabel(partner, match.partnerId);
              const text = recoTopic.question || recoTopic.title;
              return (
                <div
                  key={match.resultId}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 12,
                    fontSize: 14,
                  }}
                >
                  <span style={{ fontWeight: 700, width: 10, flexShrink: 0 }}>
                    {pSeat} {pName}
                  </span>
                  <span style={{ color: '#1a202c' }}>{text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {orderedMatches.length === 0 ? (
        <span style={{ fontSize: 14, color: '#a0aec0' }}>暂无匹配结果。</span>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              fontSize: 12,
              color: '#a0aec0',
              borderBottom: '1px solid #cbd5e0',
              paddingBottom: 4,
              marginBottom: 4,
            }}
          >
            <div style={{ width: 60, flexShrink: 0 }}>号</div>
            <div style={{ width: 100, flexShrink: 0 }}>姓名</div>
            <div style={{ flex: 1 }}>破冰话题</div>
          </div>
          {orderedMatches.map(({ match, seat: pSeat }) => {
            const partner = attendeeMap.get(match.partnerId);
            const pName = partnerLabel(partner, match.partnerId);
            const isTop = topPickIds.has(match.resultId);
            const questions = match.topics
              .filter((t) => t.type === '话题')
              .map((t) => t.question)
              .filter(Boolean)
              .slice(0, MAX_QUESTIONS_PER_ROW);
            return (
              <div
                key={match.resultId}
                style={{
                  display: 'flex',
                  padding: '8px 0',
                  borderBottom: '1px solid #f7fafc',
                  fontSize: 14,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 60,
                    flexShrink: 0,
                    fontWeight: isTop ? 700 : 400,
                  }}
                >
                  {isTop ? '★ ' : ''}
                  {pSeat}
                </div>
                <div
                  style={{
                    width: 100,
                    flexShrink: 0,
                    fontWeight: isTop ? 700 : 400,
                  }}
                >
                  {pName}
                </div>
                <div style={{ flex: 1, color: '#1a202c' }}>
                  {questions.length === 0 ? (
                    <span style={{ color: '#a0aec0' }}>—</span>
                  ) : (
                    questions.map((q, i) => (
                      <div
                        key={i}
                        style={{
                          marginBottom: i < questions.length - 1 ? 4 : 0,
                        }}
                      >
                        {q}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          fontSize: 12,
          color: '#a0aec0',
          marginTop: 24,
          paddingTop: 12,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        {FOOTER_TEXT}
      </div>
    </div>
  );
};

export default PrintCard;
